import React, { useMemo, useState } from 'react';
import { Brand } from '../components/Brand';
import { Button, Card, Progress } from '../components/Primitives';
import { useApp } from '../lib/app-context';
import type { Level } from '../types';
import { scheduleDailyStudyReminder } from '../lib/notifications';
import { edgeFetch } from '../lib/cloud';

const goals = [
  'Noldan boshlash',
  'Kundalik suhbat',
  'Ish va karyera',
  'Sayohat',
  'Imtihon uchun baza',
  'O‘zimni rivojlantirish',
];

const levels: Level[] = [
  'Pre-A1',
  'A1',
  'A2',
  'B1',
  'B2',
  'C1',
];

export function Onboarding() {
  const { state, setState, setRoute } = useApp();

  const [step, setStep] = useState(1);

  const [goal, setGoal] = useState(
    state.profile.goal || goals[0],
  );

  const [level, setLevel] = useState<Level>(
    state.profile.level || 'Pre-A1',
  );

  const [studyTime, setStudyTime] = useState(
    state.profile.studyTime || '21:00',
  );

  const [minutes, setMinutes] = useState(
    state.profile.dailyMinutes || 20,
  );

  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const percent = useMemo(
    () => step * 25,
    [step],
  );

  const finish = async () => {
if (!goal) {
      setStatus('Asosiy maqsadni tanlang.');
      return;
    }

    if (!level) {
      setStatus('Ingliz tili darajasini tanlang.');
      return;
    }

    if (!studyTime) {
      setStatus('Eslatma vaqtini kiriting.');
      return;
    }

    setSubmitting(true);
    setStatus('Onboarding yakunlanmoqda...');

    try {
      const timezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'Asia/Samarkand';

  const response = await edgeFetch(
    '/sync-state',
    {
      method: 'POST',
      body: JSON.stringify({
        action: 'complete_onboarding',
        profile: {
          goal,
          level,
          studyTime,
          dailyMinutes: minutes,
          timezone,
          strictTeacher:
            state.settings?.strictTeacher ?? true,
        },
      }),
    },
    true,
  );

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      data?.error ||
        `Onboardingni yakunlab bo'lmadi (${response.status}).`,
    );
  }

      if (!data?.ok) {
        throw new Error(
          data?.error ||
            'Onboardingni yakunlab bo‘lmadi.',
        );
      }

      setState((current) => ({
        ...current,
        profile: {
          ...current.profile,
          goal,
          level,
          studyTime,
          dailyMinutes: minutes,
          timezone,
          onboardingComplete: true,
          onboardingCompletedAt: data.onboardingCompletedAt || current.profile.onboardingCompletedAt,
          courseStartedOn: data.courseStartedOn || current.profile.courseStartedOn,
          // Trial vaqtini client yaratmaydi; server qiymati faqat local cache sifatida saqlanadi.
          trialStartedAt: data.trialStartedAt || undefined,
        },
        progress: {
          ...current.progress,
          day: Number(data.courseDay ?? 1),
          courseDate: data.courseDate || data.courseStartedOn || current.progress.courseDate,
          todayMissionCompleted: false,
          dailyCompletionDates: [],
          completedDays: [],
          missedDays: 0,
          streak: 0,
          courseFinished: false,
        },
      }));

      try {
        await scheduleDailyStudyReminder(studyTime);
      } catch {
        // Notification permission berilmagan bo‘lsa
        // onboarding baribir tugaydi.
      }

      setStatus('7 kunlik PRO trial boshlandi.');
      setRoute('dashboard');
    } catch (err) {
      setStatus(
        err instanceof Error
          ? err.message
          : 'Onboarding xatosi.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding-box">
        <div className="onboarding-brand">
          <Brand />
          <span>
            Shaxsiy 365 kunlik yo‘lingizni sozlaymiz
          </span>
        </div>

        <Progress value={percent} />

        <Card className="onboarding-card">

          {step === 1 && (
            <div className="form-step">
              <span className="step-kicker">
                01 · MAQSAD
              </span>

              <h2>Asosiy maqsadingiz nima?</h2>

              <p>
                English Forge 365 o‘quv rejasini shu
                maqsadga moslashtiradi.
              </p>

              <div className="choice-grid">
                {goals.map((g) => (
                  <button
                    type="button"
                    key={g}
                    className={
                      goal === g ? 'selected' : ''
                    }
                    onClick={() => setGoal(g)}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="form-step">
              <span className="step-kicker">
                02 · DARAJA
              </span>

              <h2>Hozirgi ingliz tili darajangiz</h2>

              <p>
                Keyinchalik placement test bu qiymatni
                yanada aniq belgilaydi.
              </p>

              <div className="level-row">
                {levels.map((l) => (
                  <button
                    type="button"
                    key={l}
                    className={
                      level === l ? 'selected' : ''
                    }
                    onClick={() => setLevel(l)}
                  >
                    <strong>{l}</strong>

                    <span>
                      {l === 'Pre-A1'
                        ? 'Boshlang‘ich'
                        : l === 'A1'
                          ? 'Basic'
                          : l === 'A2'
                            ? 'Elementary'
                            : l === 'B1'
                              ? 'Intermediate'
                              : l === 'B2'
                                ? 'Upper Intermediate'
                                : 'Advanced'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="form-step">
              <span className="step-kicker">
                03 · KUNLIK REJA
              </span>

              <h2>Har kuni qancha shug‘ullanasiz?</h2>

              <p>
                Barqaror o‘rganish uchun real kunlik
                vaqtni tanlang.
              </p>

              <label>
                Kunlik o‘qish vaqti

                <select
                  value={minutes}
                  onChange={(e) =>
                    setMinutes(Number(e.target.value))
                  }
                >
                  <option value={10}>10 daqiqa</option>
                  <option value={15}>15 daqiqa</option>
                  <option value={20}>20 daqiqa</option>
                  <option value={30}>30 daqiqa</option>
                  <option value={45}>45 daqiqa</option>
                  <option value={60}>60 daqiqa</option>
                </select>
              </label>
            </div>
          )}

          {step === 4 && (
            <div className="form-step">
              <span className="step-kicker">
                04 · ESLATMA
              </span>

              <h2>Dars eslatmasi qachon kelsin?</h2>

              <p>
                English Forge sizni har kuni shu vaqtda
                mashg‘ulotga chaqiradi.
              </p>

              <label>
                Eslatma vaqti

                <input
                  type="time"
                  value={studyTime}
                  onChange={(e) =>
                    setStudyTime(e.target.value)
                  }
                  required
                />
              </label>

              <div className="study-contract glass">
                <strong>365 kunlik Study Contract</strong>

                <p>
                  Men har kuni soat{' '}
                  <b>{studyTime}</b> da kamida{' '}
                  <b>{minutes} daqiqa</b>{' '}
                  ingliz tili bilan shug‘ullanishni
                  maqsad qilaman.
                </p>
              </div>

              <div className="trial-note">
                Onboarding yakunlangach 7 kunlik
                to‘liq PRO sinov muddati boshlanadi.
              </div>
            </div>
          )}

          {status && (
            <div className="status-message">
              {status}
            </div>
          )}

          <div className="form-actions">
            {step > 1 && (
              <Button
                variant="ghost"
                disabled={submitting}
                onClick={() =>
                  setStep((current) => current - 1)
                }
              >
                Orqaga
              </Button>
            )}

            <span />

            <Button
              disabled={submitting}
              onClick={() => {
                if (step < 4) {
                  setStep((current) => current + 1);
                } else {
                  void finish();
                }
              }}
            >
              {submitting
                ? 'Kutilmoqda...'
                : step < 4
                  ? 'Davom etish'
                  : '7 kunlik PRO ni boshlash'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}